import { colors } from '@/styles';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { Global, css } from '@emotion/react';
import LassoControl from '../Mapbox/Controls/Lasso';
import ZoomControl from '../Mapbox/Controls/Zoom';
import RadiusControl from '../Mapbox/Controls/Radius';
import FeatureClick from '../Mapbox/FeatureClick';
import Layer from '../Mapbox/Layer';
import LayerOrder from '../Mapbox/LayerOrder';
import Map from '../Mapbox/Map';
import Source from '../Mapbox/Source';
import { zipFromFeatures } from './panel/utils';
import { PANEL_INSET, PANEL_SLIDE_MS, PANEL_WIDTH } from './panel/constants';

const DEFAULT_CENTER = [-104.99, 39.74];

// A transparent fill that exists purely as a click target, at the bottom of the stack.
const HIT_LAYER = 'outlines-layer';

const SELECTED_SUBREGION_LAYER = 'selected-subregion-layer';
const HOVER_SUBREGION_LAYER = 'hover-subregion-layer';
// A hovered carrier's own regions, drawn over the profile's subregions in one fixed colour so they
// never read as another greyed-out subregion. Fill and outline are separate layers: a fill's outline
// is always 1px.
const CARRIER_REGION_LAYER = 'carrier-region-layer';
const CARRIER_REGION_OUTLINE_LAYER = 'carrier-region-outline-layer';
const CARRIER_REGION_COLOR = colors.blues.primary;
const HOVER_ZIP_LAYER = 'hover-zip-layer';
const SELECTED_ZIP_LAYER = 'selected-zip-layer';

const subregionLayerId = (subregion) => `subregion-layer-${subregion.subregion_id}`;

// Scopes the open-panel style below to this map's container.
const MAP_CLASS = 'subregion-map';

const SubregionMap = forwardRef(
    (
        {
            node,
            subregions,
            callbacks,
            highlightOverride,
            selectedSubregion,
            hoveredSubregion,
            selectedZips,
            hoveredSubregionZip,
            radiusControl,
            // From an open panel; its draft `zips` paint over the saved ones.
            zipToggle,
            // The hovered carrier's region zips; empty when no carrier is hovered.
            carrierRegionZips = [],
        },
        ref
    ) => {
        const [lassoArmed, setLassoArmed] = useState(false);

        useEffect(() => {
            node?.classList.add(MAP_CLASS);
        }, [node]);

        const draftZips = zipToggle?.zips;
        const draftActive = Array.isArray(draftZips);

        // Dropped from the base pass while drafting, or the saved fill underneath keeps a
        // just-removed zip coloured in.
        const baseSubregions = draftActive
            ? (subregions || []).filter((s) => s.subregion_id !== selectedSubregion?.subregion_id)
            : subregions || [];

        const activeZips = draftActive ? draftZips : selectedSubregion?.zips?.map((o) => o.zip) || [];

        // Bottom to top. JSX order alone does not hold once a layer unmounts and comes back.
        const layerOrderKey = baseSubregions.map((s) => s.subregion_id).join('|');
        const layerOrder = useMemo(
            () => [
                HIT_LAYER,
                ...baseSubregions.map(subregionLayerId),
                SELECTED_SUBREGION_LAYER,
                HOVER_SUBREGION_LAYER,
                CARRIER_REGION_LAYER,
                CARRIER_REGION_OUTLINE_LAYER,
                HOVER_ZIP_LAYER,
                SELECTED_ZIP_LAYER,
            ],
            [layerOrderKey]
        );

        return (
            <Map ref={ref} node={node} center={DEFAULT_CENTER} zoom={7} minZoom={5} callbacks={callbacks}>
                {({ map }) => {
                    return (
                        <>
                            {/* zipToggle is only passed while the panel is open. The panel covers the
                                map's top-left corner, so the zoom control there moves to just right of
                                it, in step with the panel sliding in. */}
                            <Global
                                styles={css`
                                    .${MAP_CLASS} .mapboxgl-ctrl-top-left {
                                        left: ${zipToggle ? PANEL_INSET + PANEL_WIDTH : 0}px;
                                        transition: left ${PANEL_SLIDE_MS}ms ease-out;
                                        @media (prefers-reduced-motion: reduce) {
                                            transition: none;
                                        }
                                    }
                                `}
                            />
                            <Source map={map} url="mapbox://kevinmwalsh.zcta-2020" name="zcta.2020" />
                            <ZoomControl map={map} location="top-left" />
                            {radiusControl ? <RadiusControl map={map} callbacks={radiusControl.callbacks} /> : null}
                            {zipToggle ? (
                                <FeatureClick
                                    map={map}
                                    layer={HIT_LAYER}
                                    enabled={!lassoArmed}
                                    onClick={(features) => zipToggle.onToggle(zipFromFeatures(features))}
                                    onHover={(features) => callbacks.setZipHover?.(zipFromFeatures(features))}
                                />
                            ) : null}
                            <LassoControl
                                map={map}
                                callbacks={{
                                    onToggle: setLassoArmed,
                                    onClear: () => {
                                        callbacks.selectObject((prev) => {
                                            return {
                                                ...prev,
                                                zip: {},
                                            };
                                        });
                                    },
                                    onSelect: (start, end) => {
                                        const features = map.queryRenderedFeatures([start, end], {
                                            layers: [HIT_LAYER],
                                        });

                                        callbacks.selectObject((prev) => {
                                            const next = features.reduce(
                                                (acc, feature) => {
                                                    acc[feature.properties.NAME20] = acc[feature.properties.NAME20]
                                                        ? !acc[feature.properties.NAME20]
                                                        : true;

                                                    return acc;
                                                },
                                                { ...prev.zip }
                                            );

                                            return {
                                                ...prev,
                                                zip: next,
                                            };
                                        });
                                    },
                                }}
                            />
                            <Layer
                                id={HIT_LAYER}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="fill"
                                paint={{
                                    'fill-color': 'rgba(0,0,0,0)',
                                    'fill-outline-color': '#000',
                                }}
                            />
                            {baseSubregions.map((subregion) => (
                                <Layer
                                    id={subregionLayerId(subregion)}
                                    key={subregion.subregion_id}
                                    map={map}
                                    source="zcta.2020"
                                    source-layer="zctas"
                                    type="fill"
                                    paint={{
                                        'fill-color': highlightOverride
                                            ? callbacks.genColor(subregion || {}) || 'rgba(0,0,0,0)'
                                            : '#4C4C4C',
                                        'fill-opacity': highlightOverride ? 0.7 : 0.3,
                                        'fill-outline-color': '#000',
                                    }}
                                    filter={['in', 'NAME20', ...subregion.zips.map((zip) => zip.zip)]}
                                />
                            ))}
                            <Layer
                                id={SELECTED_SUBREGION_LAYER}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="fill"
                                paint={{
                                    // A new draft has no id to look a colour up by, so it falls back to green.
                                    'fill-color': callbacks.genColor(selectedSubregion || {}) || colors.greens.primary,
                                    'fill-opacity': 0.7,
                                    'fill-outline-color': '#000',
                                }}
                                filter={['in', 'NAME20', ...activeZips]}
                            />
                            <Layer
                                id={HOVER_SUBREGION_LAYER}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="fill"
                                paint={{
                                    'fill-color': callbacks.genColor(hoveredSubregion || {}) || 'rgba(0,0,0,0)',
                                    'fill-opacity': 0.7,
                                    'fill-outline-color': '#000',
                                }}
                                filter={['in', 'NAME20', ...(hoveredSubregion?.zips.map((o) => o.zip) || [])]}
                            />
                            {/* Always mounted, like the hover layer: an empty filter draws nothing, and
                                hovering doesn't add and remove layers. */}
                            <Layer
                                id={CARRIER_REGION_LAYER}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="fill"
                                paint={{
                                    'fill-color': CARRIER_REGION_COLOR,
                                    'fill-opacity': 0.25,
                                }}
                                filter={['in', 'NAME20', ...carrierRegionZips]}
                            />
                            <Layer
                                id={CARRIER_REGION_OUTLINE_LAYER}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="line"
                                paint={{
                                    'line-color': CARRIER_REGION_COLOR,
                                    'line-width': 2,
                                }}
                                filter={['in', 'NAME20', ...carrierRegionZips]}
                            />
                            <Layer
                                id={HOVER_ZIP_LAYER}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="fill"
                                paint={{
                                    'fill-color': '#000',
                                    'fill-opacity': 0.2,
                                    'fill-outline-color': '#000',
                                }}
                                filter={['in', 'NAME20', ...(hoveredSubregionZip ? [hoveredSubregionZip] : [])]}
                            />
                            <Layer
                                id={SELECTED_ZIP_LAYER}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="fill"
                                paint={{
                                    'fill-color': colors.greens.primary,
                                    'fill-opacity': 0.7,
                                    'fill-outline-color': '#000',
                                }}
                                filter={['in', 'NAME20', ...selectedZips]}
                            />
                            {/* Last, so it runs after every Layer above has added. */}
                            <LayerOrder map={map} ids={layerOrder} />
                        </>
                    );
                }}
            </Map>
        );
    }
);

export default SubregionMap;
